import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useChat } from '../contexts/ChatContext';
import ChatBubble from '../components/ChatBubble';

export default function ChatScreen() {
  const { id: convId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const conversation = location.state?.conversation;

  const { messages, loadMessages, sendMessage, markRead, sendTyping, getUser, cacheUser, isTyping, currentUserId } = useChat();

  const [text, setText] = useState('');
  const [isTypingLocal, setIsTypingLocal] = useState(false);
  const typingTimer = useRef(null);
  const scrollRef = useRef(null);
  const prevCount = useRef(0);

  const convMessages = messages[convId] || [];

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 100);
  }, []);

  useEffect(() => {
    loadMessages(convId).then(() => scrollToBottom());
    markRead(convId);
  }, [convId]);

  useEffect(() => {
    if (convMessages.length > prevCount.current && prevCount.current > 0) {
      scrollToBottom();
    }
    prevCount.current = convMessages.length;
  }, [convMessages.length, scrollToBottom]);

  const otherId = conversation
    ? conversation.participants.find((p) => p !== currentUserId)
    : null;
  const otherUser = otherId ? getUser(otherId) : null;
  const displayName = conversation?.isGroup
    ? conversation.groupName || 'Group'
    : otherUser?.displayName || otherUser?.username || 'Unknown';

  useEffect(() => {
    if (otherId) cacheUser(otherId);
  }, [otherId, cacheUser]);

  const handleSend = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    sendMessage(convId, trimmed);
    setText('');
    if (isTypingLocal) {
      sendTyping(convId, false);
      setIsTypingLocal(false);
    }
    if (typingTimer.current) clearTimeout(typingTimer.current);
    setTimeout(scrollToBottom, 100);
  };

  const handleTyping = (e) => {
    setText(e.target.value);
    if (!isTypingLocal) {
      setIsTypingLocal(true);
      sendTyping(convId, true);
    }
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      setIsTypingLocal(false);
      sendTyping(convId, false);
    }, 2000);
  };

  const isOtherTyping = otherId ? isTyping(convId, otherId) : false;

  return (
    <div className="chat-screen">
      <div className="chat-header">
        <button className="back-btn" onClick={() => navigate('/')}>←</button>
        <div className="chat-header-info">
          <div className="chat-header-name">{displayName}</div>
        </div>
        {!conversation?.isGroup && (
          <div className="chat-call-actions">
            <button
              className="call-btn"
              onClick={() => navigate(`/call/${otherId}`, { state: { isVideo: false, isCaller: true } })}
              title="Voice call"
            >
              📞
            </button>
            <button
              className="call-btn"
              onClick={() => navigate(`/call/${otherId}`, { state: { isVideo: true, isCaller: true } })}
              title="Video call"
            >
              📹
            </button>
          </div>
        )}
      </div>

      <div className="chat-messages" ref={scrollRef}>
        {convMessages.length === 0 ? (
          <div className="empty-state">No messages yet. Say hello!</div>
        ) : (
          convMessages.map((msg) => (
            <ChatBubble
              key={msg._id}
              message={msg}
              isMine={msg.senderId === currentUserId}
            />
          ))
        )}
      </div>

      {isOtherTyping && <div className="typing-indicator">typing...</div>}

      <form className="chat-input-bar" onSubmit={handleSend}>
        <input
          className="chat-input"
          type="text"
          placeholder="Type a message"
          value={text}
          onChange={handleTyping}
        />
        <button className="send-btn" type="submit" disabled={!text.trim()}>
          ➤
        </button>
      </form>
    </div>
  );
}
